import type {
    CodedLabel,
    OutcomeAssignment,
    OutcomeTarget,
    TaskOutcome,
} from "@entities/generation-task";

/**
 * 結果情報から一覧の群を組み立てる（純関数）。
 *
 * **1 行は結果のタイトル 1 つ**とし、行は担当と 1 対 1 になる。枠は行ではなく行を開いたときの
 * 中身で、並びの単位にはしない。グルーピングはスライドで行う
 * （[[2026-09-10 設計ノート アドイン 生成結果画面の結果情報対応]]）。
 *
 * **Web コンソールと同じ振る舞いにするが、実装は共有しない。** 別パッケージで共有の置き場が
 * UI プリミティブしか無く、判定ロジックをそこへ置くのは所有の筋が通らない。揃えるのは振る舞いで
 * あって実装ではない（`filler` が `template_filler` を複製したのと同じ判断）。
 */

/** 群の鍵。枚が引けない担当と、複数の枚にまたがる担当の行き先。 */
const UNKNOWN_SLIDE = "__unknown_slide__";
const MULTI_SLIDE = "__multi_slide__";

/** 枠 1 つぶんの欄。行の中身であって行ではない。 */
export type OutcomeTargetRow = {
    targetRef: string;
    /** 表示名。名前が無ければ枠の位置から組んだ仮の名前。 */
    name: string;
    /** 名前を持っているか。 */
    named: boolean;
    slideRef: string | null;
    slideNo: number | null;
    write: CodedLabel;
    pattern: CodedLabel | null;
    grounds: OutcomeTarget["grounds"];
    /** 確認済みの記録キー。**枠の識別子そのもの**で、Web コンソールと記録を共有する。 */
    checkKey: string;
};

/** 行の右端に出す印。**依頼が付いているものが手を動かす合図**で、それ以外は結果の事実である。 */
export type OutcomeMark = {
    label: string;
    alert: boolean;
};

export type OutcomeRow = {
    key: string;
    assignmentId: string;
    name: string;
    /** 主テキスト。結果タイトル。無ければ担当の名前へ落ちる。 */
    headline: string;
    state: CodedLabel;
    patterns: CodedLabel[];
    report: string;
    requestPatterns: CodedLabel[];
    request: string;
    sources: OutcomeAssignment["sources"];
    targets: OutcomeTargetRow[];
    /** 代表の枚。PowerPoint を飛ばす先。 */
    slideNo: number | null;
    marks: OutcomeMark[];
};

export type OutcomeGroup = {
    key: string;
    title: string;
    rows: OutcomeRow[];
    /** この群の依頼がある行数（見出し右の重み）。 */
    alertCount: number;
};

/** 絞り込み。**上書きの軸は持たない。** アドインは settings を直接直すので上書きの概念が無い。 */
export type OutcomeFilter = "all" | "alert" | "grounds";
/** 次の再実行でどうなるか。手元の settings が持つ旗から出す。 */
export type RunFilter = "all" | "skip" | "run";

function slideOf(targets: OutcomeTargetRow[]): { key: string; slideNo: number | null } {
    const refs = new Set(targets.map((t) => t.slideRef).filter((r): r is string => !!r));
    if (refs.size === 0) return { key: UNKNOWN_SLIDE, slideNo: null };
    if (refs.size > 1) return { key: MULTI_SLIDE, slideNo: null };
    const only = Array.from(refs)[0]!;
    const withNo = targets.find((t) => t.slideRef === only && t.slideNo != null);
    return { key: only, slideNo: withNo?.slideNo ?? null };
}

/**
 * 枠の表示名。
 *
 * 名前はサーバーが載せるが、付いていない枠がある。**そのときは位置から仮の名前を組む。**
 * 識別子を出すと読み手の語彙から外れる。
 */
function nameOfTarget(target: OutcomeTarget, index: number): { name: string; named: boolean } {
    const name = (target.name ?? "").trim();
    if (name) return { name, named: true };
    const place = target.slideNo ? `スライド ${target.slideNo} の枠` : "枠";
    return { name: `${place} (${index + 1})`, named: false };
}

/**
 * 行の印。
 *
 * 順序を固定する。**依頼 → 結果パターン**で、手を動かす合図が先に目へ入る。上書きの印は
 * 持たない（settings を直接直すので上書きという状態が無い）。
 */
function buildMarks(assignment: OutcomeAssignment): OutcomeMark[] {
    const marks: OutcomeMark[] = [];
    for (const request of assignment.requestPatterns ?? []) {
        marks.push({ label: "▲" + request.label, alert: true });
    }
    for (const pattern of assignment.patterns ?? []) {
        marks.push({ label: pattern.label, alert: false });
    }
    return marks;
}

/**
 * 枠を並べる順。**根拠のあるものを先に置く。**
 *
 * 行が担当になると枠を単位に絞り込めなくなるので、絞り込みを二重に持つ代わりに並び順で応える。
 */
function sortTargets(rows: OutcomeTargetRow[]): OutcomeTargetRow[] {
    return [...rows].sort((a, b) => {
        const ag = a.grounds.length > 0 ? 0 : 1;
        const bg = b.grounds.length > 0 ? 0 : 1;
        if (ag !== bg) return ag - bg;
        const an = a.slideNo ?? Number.MAX_SAFE_INTEGER;
        const bn = b.slideNo ?? Number.MAX_SAFE_INTEGER;
        if (an !== bn) return an - bn;
        return a.name.localeCompare(b.name, "ja");
    });
}

export function buildOutcomeRows(outcome: TaskOutcome | null | undefined): OutcomeGroup[] {
    const groups = new Map<string, OutcomeGroup>();

    for (const assignment of outcome?.assignments ?? []) {
        const targets = sortTargets(
            (assignment.targets ?? []).map((target, i) => {
                const { name, named } = nameOfTarget(target, i);
                return {
                    targetRef: target.targetRef,
                    name,
                    named,
                    slideRef: target.slideRef,
                    slideNo: target.slideNo,
                    write: target.write,
                    pattern: target.pattern,
                    grounds: target.grounds ?? [],
                    // **枠の識別子をそのまま記録キーにする。** Web コンソールと同じ形なので、
                    // どちらで付けたチェックも互いに見える。
                    checkKey: target.targetRef,
                };
            })
        );
        const place = slideOf(targets);
        const row: OutcomeRow = {
            key: assignment.assignmentId,
            assignmentId: assignment.assignmentId,
            name: assignment.name ?? "",
            // タイトルが無いのは報告の段を呼べなかった担当。担当の名前へ落とす。
            headline: (assignment.title ?? "").trim() || assignment.name || assignment.assignmentId,
            state: assignment.state,
            patterns: assignment.patterns ?? [],
            report: assignment.report ?? "",
            requestPatterns: assignment.requestPatterns ?? [],
            request: assignment.request ?? "",
            sources: assignment.sources ?? [],
            targets,
            slideNo: place.slideNo,
            marks: buildMarks(assignment),
        };
        const title =
            place.key === MULTI_SLIDE
                ? "複数のスライド"
                : place.key === UNKNOWN_SLIDE || !place.slideNo
                  ? "スライド未特定"
                  : "スライド " + place.slideNo;
        const group = groups.get(place.key) ?? { key: place.key, title, rows: [], alertCount: 0 };
        group.rows.push(row);
        if (row.marks.some((m) => m.alert)) group.alertCount += 1;
        groups.set(place.key, group);
    }

    // 枚の番号順。番号が無い群（複数・未特定）は末尾へ置く。
    return Array.from(groups.values()).sort((a, b) => {
        const an = a.rows[0]?.slideNo ?? Number.MAX_SAFE_INTEGER;
        const bn = b.rows[0]?.slideNo ?? Number.MAX_SAFE_INTEGER;
        return an - bn;
    });
}

/**
 * 行が絞り込みに残るか。
 *
 * 要確認は依頼があるか、根拠はいずれかの枠に根拠があるか。**次の再実行でどうなるかは手元の
 * settings が持つ旗**で見るので、判定に要る `skip` は呼び出し側が渡す。
 */
export function keepRow(
    row: OutcomeRow,
    filter: OutcomeFilter,
    runFilter: RunFilter,
    skip: boolean
): boolean {
    if (filter === "alert" && !row.marks.some((m) => m.alert)) return false;
    if (filter === "grounds" && !row.targets.some((t) => t.grounds.length > 0)) return false;
    if (runFilter === "skip" && !skip) return false;
    if (runFilter === "run" && skip) return false;
    return true;
}

/** 一覧の重み。 */
export type OutcomeTotals = {
    rows: number;
    alert: number;
    grounds: number;
    /** 枠の総数。**確認の進捗の分母**はこちらで、担当の数ではない。 */
    targets: number;
};

export function countOutcomeRows(groups: OutcomeGroup[]): OutcomeTotals {
    const rows = groups.flatMap((g) => g.rows);
    return {
        rows: rows.length,
        alert: rows.filter((r) => r.marks.some((m) => m.alert)).length,
        grounds: rows.filter((r) => r.targets.some((t) => t.grounds.length > 0)).length,
        targets: rows.reduce((n, r) => n + r.targets.length, 0),
    };
}
